import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, CalendarRange, HandCoins, Package } from 'lucide-react';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { DETAIL_TABLE_WIDER_DATES_HINT } from '@/components/common/detailTableLayout.constants';
import { LabeledPillList, type PillField } from '@/components/common/LabeledPill';
import { PrintButton } from '@/components/common/PrintButton';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import {
  donationCandidateLinesQueryOptions,
  zonalManagerCandidateQueryOptions,
} from '../api/marketPromotionP2.api';
import {
  DONATION_LINE_DATE_COLUMN_ID,
  getDonationCandidateLineColumns,
} from '../components/DonationCandidateLineColumns';
import { DonationTopSheetPrintReport } from '../components/DonationTopSheetPrintReport';
import type {
  DonationCandidate,
  DonationCandidateLine,
  ZonalManagerDonationsSearch,
} from '../types/marketPromotionP2.types';

const NEW_BATCH_PATH = '/market-promotion/publish-panel/new';

function getSummaryPills(
  zonalManager: DonationCandidate | null | undefined,
  lines: DonationCandidateLine[] | undefined,
  search: ZonalManagerDonationsSearch,
): PillField[] {
  const isLoading = zonalManager === undefined || lines === undefined;
  const amount = lines?.reduce((total, line) => total + line.amount, 0);
  return [
    {
      key: 'project',
      icon: Package,
      label: 'Donation Name',
      value: zonalManager?.itemName ?? undefined,
      isLoading,
    },
    {
      key: 'period',
      icon: CalendarRange,
      label: 'Dates',
      value: `${formatDisplayDate(search.dateFrom)} – ${formatDisplayDate(search.dateTo)}`,
      isLoading: false,
    },
    {
      key: 'donations',
      icon: HandCoins,
      label: 'Donations',
      value: lines === undefined ? undefined : formatNumber(lines.length),
      isLoading,
    },
    {
      key: 'amount',
      icon: Banknote,
      label: 'Amount',
      value:
        amount === undefined ? undefined : `${TAKA_SYMBOL} ${formatNumber(Math.round(amount))}`,
      isLoading,
    },
  ];
}

interface ZonalManagerDonationsPageProps {
  userId: number;
  search: ZonalManagerDonationsSearch;
}

export function ZonalManagerDonationsPage({ userId, search }: ZonalManagerDonationsPageProps) {
  const navigate = useNavigate();
  useRememberViewedRow('donationCandidates', userId, search.promoItemId);

  const params = useMemo(() => ({ ...search, userId }), [search, userId]);
  const zonalManagerQuery = useQuery(zonalManagerCandidateQueryOptions(params));
  const linesQuery = useQuery(donationCandidateLinesQueryOptions(params));
  const columns = useMemo(() => getDonationCandidateLineColumns(), []);

  const zonalManager = zonalManagerQuery.data;
  const lines = linesQuery.data;

  return (
    <>
      <DetailTableLayout
        className="print:hidden"
        title={zonalManagerQuery.isLoading ? undefined : (zonalManager?.fullName ?? 'ZM')}
        actions={<PrintButton disabled={lines === undefined || lines.length === 0} />}
        onNoHistory={() => void navigate({ to: NEW_BATCH_PATH, search })}
        summary={<LabeledPillList fields={getSummaryPills(zonalManager, lines, search)} />}
        table={{
          rows: lines ?? [],
          columns,
          isLoading: linesQuery.isLoading,
          error: linesQuery.error ?? zonalManagerQuery.error,
          errorTitle: "Could not load this ZM's donations",
          onRetry: () => {
            void zonalManagerQuery.refetch();
            void linesQuery.refetch();
          },
          stickyColumnId: DONATION_LINE_DATE_COLUMN_ID,
          emptyMessage: 'No donation of this ZM is waiting for a batch in these dates',
          emptyHint: DETAIL_TABLE_WIDER_DATES_HINT,
        }}
      />
      <DonationTopSheetPrintReport
        zonalManager={zonalManager}
        lines={lines ?? []}
        dateFrom={search.dateFrom}
        dateTo={search.dateTo}
      />
    </>
  );
}
